import { v4 } from 'uuid';

/**
 * Recursively flat map the items
 * @param {Array} items the items
 * @param {Array} arr the array
 * @param {boolean} preserveChildren if the children should be preserved
 * @returns {Array} the flat array
 */
export const recursiveFlatMap = (items, arr = [], preserveChildren = false) => {
  if (!items || !Array.isArray(items)) return [];

  items.forEach((item) => {
    const { children, ...rest } = item;
    if (children?.length > 0) {
      recursiveFlatMap(children, arr, preserveChildren);
    }
    if (preserveChildren) {
      arr.push({ ...rest, children });
    } else {
      arr.push(rest);
    }
  });
  return arr;
};

/**
 * Recursively set the order of the options
 * @param {object[]} options the list of options
 * @returns {object[]} the options with the order set
 */
export const recursiveSetOptionOrder = (options) =>
  options.map((option, index) => {
    const retVal = option;
    retVal.order = index + 1;
    if (retVal.children) {
      retVal.children = recursiveSetOptionOrder(retVal.children);
    }
    return retVal;
  });

/**
 * Recursively duplicate the product options and set a new ID
 * @param {object} options the options
 * @param {string} parentId the parent ID
 * @returns {Array} the duplicated options
 */
export const recursiveDuplicateProductOptions = (
  options,
  parentId = undefined,
) =>
  options.map((option) => {
    const id = v4();
    const { children, ...rest } = option;
    return {
      ...rest,
      id,
      parent_id: parentId,
      children:
        children.length > 0
          ? recursiveDuplicateProductOptions(children, id)
          : [],
    };
  });

/**
 * Recursively duplicate the product options and set a new ID
 * @param {object} options the options
 * @param {string} parentId the parent ID
 * @returns {Array} the duplicated options
 */
export const recursiveTemplateToProductOptions = (
  options,
  parentId = undefined,
) =>
  options.map((option) => {
    const id = v4();

    const productOption = option;
    productOption.template_to_option_link_id = option.id;
    delete productOption.template_id;
    const { children, ...rest } = productOption;

    return {
      ...rest,
      id,
      parent_id: parentId,
      children:
        children.length > 0
          ? recursiveTemplateToProductOptions(children, id)
          : [],
    };
  });

/**
 * Create a flat array of options
 * @param {Array} productOptions the options from the form
 * @param {Array} options the options from the previous state
 * @returns {{add: *[]}|{add: *[], remove: *[], update: *[]}}
 */
const createFlatOptions = (productOptions, options = []) => {
  const addOnly = options.length === 0;
  const orderOptions = recursiveSetOptionOrder(productOptions);
  const flatOptions = recursiveFlatMap(orderOptions, []);
  if (addOnly) {
    return { add: flatOptions };
  }
  // Here we need to compare the productOptions with the options to see what was added / removed / updated
  const flatPrevOptions = recursiveFlatMap(options, []);

  // Find the options that were added
  const add = flatOptions.filter(
    (option) =>
      !flatPrevOptions.find((prevOption) => prevOption.id === option.id),
  );

  // Find the options that were removed
  const removedOptions = flatPrevOptions
    .filter(
      (prevOption) =>
        !flatOptions.find((option) => prevOption.id === option.id),
    )
    .map((option) => option.id);

  // We need to make sure that all the children are also removed.

  const removedChildren =
    removedOptions?.length > 0
      ? flatPrevOptions
          ?.filter((prevOption) =>
            removedOptions?.find((option) => option === prevOption.parent_id),
          )
          ?.map((option) => option.id)
      : [];

  // Remove duplicated ids
  const remove = [...new Set([...removedChildren, ...removedOptions])];

  // Find the options that were updated
  const toUpdate = flatOptions.filter((option) => {
    const prevOption = flatPrevOptions.find((prev) => prev.id === option.id);
    return prevOption && JSON.stringify(prevOption) !== JSON.stringify(option);
  });

  const update = toUpdate.map((option) => {
    const prevOption = flatPrevOptions.find((prev) => prev.id === option.id);
    const dependencies = {
      add: [],
      remove: [],
    };
    dependencies.add = option.dependencies.filter((dep) => {
      const x = !prevOption.dependencies.find((prevDep) => prevDep === dep);
      return x;
    });
    dependencies.remove = prevOption.dependencies.filter(
      (prevDep) => !option.dependencies.find((dep) => prevDep === dep),
    );
    return { ...option, dependencies };
  });

  return { add, remove, update };
};

/**
 * Convert the template to a product
 * @param {object} template the template
 * @param {string} templateId the template ID
 * @returns {object} the product details
 */
export const convertTemplateToProduct = (template, templateId) => {
  const templateData = template;
  delete templateData.data.template.id;
  delete templateData.data.template.name;

  const productDetails = {
    product: {
      ...templateData.data.template,
      template_id: templateId,
      overrides: {},
    },
    productLinks:
      templateData.data.templateLinks?.map((link) => {
        const productLink = link;
        productLink.template_to_product_link_id = link.id;
        productLink.id = v4();
        delete productLink.template_id;
        return productLink;
      }) ?? [],
    productOptions: recursiveDuplicateProductOptions(
      recursiveTemplateToProductOptions(
        templateData.data.templateOptions ?? [],
      ),
    ),
    productGallery:
      templateData.data.templateGallery?.map((gallery) => {
        const productImage = gallery;
        productImage.template_gallery_id = gallery.id;
        productImage.id = v4();
        delete productImage.template_id;
        return productImage;
      }) ?? [],
    productDocuments:
      templateData.data.templateDocuments?.map((document) => {
        const productDocument = document;
        productDocument.template_document_id = document.id;
        productDocument.id = v4();
        delete productDocument.template_id;
        return productDocument;
      }) ?? [],
    categories:
      templateData.data.categories?.map((category) => category.category_id) ??
      [],
  };

  return productDetails;
};

export default createFlatOptions;
